import axios from "axios"
import type { NextResponse } from "next/server"
import { env } from "@/env"

export interface AddCartItemInput {
  productId: number
  quantity: number
  variationId?: number
  attributes?: Record<string, string>
}

export interface AddCartItemResult {
  ok: boolean
  cartCount: number
  notices: string[]
  fragments: Record<string, string>
  sessionToken?: string
  upstreamSetCookie: string[]
  cartToken: string | null
}

export interface CartLineItem {
  key: string
  productId: number
  name: string
  quantity: number
  price: number
  lineTotal: number
  thumbnail?: string
}

export interface CartCoupon {
  code: string
  discountTotal: number
}

export interface CartShippingAddress {
  country: string
  state: string
  city: string
  postcode: string
}

export interface CartSnapshot {
  ok: boolean
  cartCount: number
  items: CartLineItem[]
  totals: {
    subtotal: number
    discountTotal: number
    total: number
    currency: string
  }
  coupons: CartCoupon[]
  shippingAddress: CartShippingAddress
  upstreamSetCookie: string[]
  cartToken: string | null
}

export interface UpdateCartItemQuantityInput {
  key: string
  quantity: number
}

export interface RemoveCartItemInput {
  key: string
}

export interface CartMutationResult {
  ok: boolean
  upstreamSetCookie: string[]
  cartToken: string | null
}

export interface CartCouponInput {
  code: string
}

export interface CartShippingAddressInput {
  country: string
  state: string
  city: string
  postcode: string
}

export interface CartCouponMutationResult extends CartMutationResult {
  message?: string
}

export interface CartShippingAddressMutationResult extends CartMutationResult {
  message?: string
}

export interface NamedCookie {
  name: string
  value: string
}

const isProductionEnvironment = process.env.NODE_ENV === "production"
const WOO_CONTEXT_COOKIE_NAMES = [
  "woocommerce_cart_hash",
  "woocommerce_items_in_cart",
] as const

const isWooContextCookieName = (cookieName: string): boolean => {
  return (
    cookieName.startsWith("wp_woocommerce_session_") ||
    WOO_CONTEXT_COOKIE_NAMES.includes(
      cookieName as (typeof WOO_CONTEXT_COOKIE_NAMES)[number]
    )
  )
}

const parseSetCookiePair = (raw: string): NamedCookie | null => {
  const pair = raw.split(";")[0]?.trim()

  if (!pair) {
    return null
  }

  const [name, ...valueParts] = pair.split("=")

  if (!name || valueParts.length === 0) {
    return null
  }

  return {
    name,
    value: valueParts.join("="),
  }
}

export const extractWooCartSessionCookies = (
  setCookieHeaders: string[]
): NamedCookie[] => {
  return setCookieHeaders
    .map(parseSetCookiePair)
    .filter((cookie): cookie is NamedCookie => Boolean(cookie))
    .filter((cookie) => isWooContextCookieName(cookie.name))
}

interface BuildForwardCookieHeaderInput {
  authSessionToken: string | null
  wooGuestSessionCookies: NamedCookie[]
}

const COOKIE_PAIR_REGEX = /^[^=\s;]+=[^;]*$/

const tryDecodeURIComponent = (value: string): string => {
  try {
    return decodeURIComponent(value)
  } catch {
    return value
  }
}

const parseCookiePairs = (rawValue: string | null): string[] => {
  const normalizedValue = rawValue?.trim()

  if (!normalizedValue) {
    return []
  }

  const decodedValue = tryDecodeURIComponent(normalizedValue)

  return decodedValue
    .split(";")
    .map((item) => item.trim())
    .filter((item) => COOKIE_PAIR_REGEX.test(item))
}

export const buildForwardCookieHeader = ({
  authSessionToken,
  wooGuestSessionCookies,
}: BuildForwardCookieHeaderInput): string => {
  const parts: string[] = parseCookiePairs(authSessionToken)

  for (const cookie of wooGuestSessionCookies) {
    parts.push(`${cookie.name}=${cookie.value}`)
  }

  return parts.join("; ")
}

interface CookieStoreLike {
  getAll: () => Array<{ name: string; value: string }>
  get: (name: string) => { name: string; value: string } | undefined
}

export const readWooGuestSessionCookies = (
  cookieStore: CookieStoreLike
): NamedCookie[] => {
  return cookieStore
    .getAll()
    .filter((cookie) => isWooContextCookieName(cookie.name))
    .map(({ name, value }) => ({ name, value }))
}

export const readCartTokenCookie = (
  cookieStore: CookieStoreLike
): string | null => {
  return cookieStore.get("woocommerce_store_api_cart_token")?.value ?? null
}

export const applyWooCartSessionCookies = (
  response: NextResponse,
  setCookieHeaders: string[]
): void => {
  const cookies = extractWooCartSessionCookies(setCookieHeaders)

  for (const cookie of cookies) {
    response.cookies.set(cookie.name, cookie.value, {
      httpOnly: true,
      secure: isProductionEnvironment,
      sameSite: "lax",
      path: "/",
    })
  }
}

export const applyCartTokenCookie = (
  response: NextResponse,
  cartToken: string | null
): void => {
  if (!cartToken) {
    return
  }

  response.cookies.set("woocommerce_store_api_cart_token", cartToken, {
    httpOnly: true,
    secure: isProductionEnvironment,
    sameSite: "lax",
    path: "/",
  })
}

export const toWordPressOrigin = (url: string): string => {
  return new URL(url).origin
}

const CART_COUNT_REGEX = /xoo-wsc-items-count[^>]*>(\d+)</
const APPLY_COUPON_NONCE_REGEX = /"apply_coupon_nonce":"([^"]+)"/
const REMOVE_COUPON_NONCE_REGEX = /"remove_coupon_nonce":"([^"]+)"/
const SHIPPING_CALCULATOR_NONCE_REGEXES = [
  /"woocommerce-shipping-calculator-nonce":"([^"]+)"/,
  /name=["']woocommerce-shipping-calculator-nonce["'][^>]*value=["']([^"']+)["']/,
]
const HTML_LIST_ITEM_REGEX = /<li[^>]*>([\s\S]*?)<\/li>/i
const HTML_TAG_REGEX = /<[^>]+>/g
const WOOCOMMERCE_NOTICE_BLOCK_REGEX =
  /<(ul|div)[^>]*class=["'][^"']*(?:woocommerce-message|woocommerce-error|woocommerce-info|is-error)[^"']*["'][^>]*>[\s\S]*?<\/\1>/i

const HTML_NAMED_ENTITIES: Record<string, string> = {
  amp: "&",
  apos: "'",
  gt: ">",
  lt: "<",
  nbsp: " ",
  quot: '"',
}

interface WooStoreCartItem {
  key?: string
  id?: number
  name?: string
  quantity?: number
  images?: Array<{ src?: string }>
  prices?: {
    price?: string
    currency_code?: string
    currency_minor_unit?: number
  }
  totals?: {
    line_total?: string
  }
}

interface WooStoreCartResponse {
  coupons?: Array<{
    code?: string
    totals?: {
      total_discount?: string
    }
  }>
  items?: WooStoreCartItem[]
  totals?: {
    total_items?: string
    total_discount?: string
    total_items_tax?: string
    total_price?: string
    total_tax?: string
    currency_code?: string
    currency_minor_unit?: number
  }
  shipping_address?: {
    country?: string
    state?: string
    city?: string
    postcode?: string
  }
}

const normalizeHeaderValues = (
  value: string | string[] | undefined
): string[] => {
  if (Array.isArray(value)) {
    return value
  }

  if (value) {
    return [value]
  }

  return []
}

const normalizeHeaderValue = (
  value: string | string[] | undefined
): string | null => {
  if (Array.isArray(value)) {
    return value[0] ?? null
  }

  return value ?? null
}

const normalizeResponseMetadata = (headers: {
  [key: string]: string | string[] | undefined
}): {
  upstreamSetCookie: string[]
  cartToken: string | null
} => {
  return {
    upstreamSetCookie: normalizeHeaderValues(headers["set-cookie"]),
    cartToken: normalizeHeaderValue(headers["cart-token"]),
  }
}

const buildWordPressRequestHeaders = (
  sessionToken: string,
  cartToken?: string | null,
  contentType?: string
): Record<string, string> => {
  const headers: Record<string, string> = {}

  if (contentType) {
    headers["Content-Type"] = contentType
  }

  if (sessionToken.trim()) {
    headers.Cookie = sessionToken
  }

  if (cartToken?.trim()) {
    headers["Cart-Token"] = cartToken
  }

  return headers
}

export const toDecimalCurrency = (
  value: string | undefined,
  minorUnit: number
): number => {
  if (!value) {
    return 0
  }

  const parsedInteger = Number.parseInt(value, 10)

  if (Number.isNaN(parsedInteger)) {
    return 0
  }

  return parsedInteger / 10 ** minorUnit
}

const normalizeCount = (fragments: Record<string, string>): number => {
  const mergedFragments = Object.values(fragments).join(" ")
  const matchedCount = mergedFragments.match(CART_COUNT_REGEX)

  if (!matchedCount?.[1]) {
    return 0
  }

  return Number.parseInt(matchedCount[1], 10)
}

const extractCartNonce = (
  html: string,
  matcher: RegExp | RegExp[]
): string | null => {
  const matchers = Array.isArray(matcher) ? matcher : [matcher]

  for (const nonceMatcher of matchers) {
    const match = html.match(nonceMatcher)
    if (match?.[1]) {
      return match[1]
    }
  }

  return null
}

const toNoticeMessage = (html: string): string | null => {
  const noticeBlock = html.match(WOOCOMMERCE_NOTICE_BLOCK_REGEX)?.[0]

  if (!noticeBlock) {
    return null
  }

  const match = noticeBlock.match(HTML_LIST_ITEM_REGEX)

  const candidateText = match?.[1] ?? noticeBlock

  const normalized = candidateText
    .replace(HTML_TAG_REGEX, " ")
    .replace(/&(#x?[\da-f]+|[a-z]+);/gi, (entity, token: string) => {
      if (token.startsWith("#x") || token.startsWith("#X")) {
        const codePoint = Number.parseInt(token.slice(2), 16)

        return Number.isNaN(codePoint)
          ? entity
          : String.fromCodePoint(codePoint)
      }

      if (token.startsWith("#")) {
        const codePoint = Number.parseInt(token.slice(1), 10)

        return Number.isNaN(codePoint)
          ? entity
          : String.fromCodePoint(codePoint)
      }

      return HTML_NAMED_ENTITIES[token.toLowerCase()] ?? entity
    })
    .replace(/\s+/g, " ")
    .trim()

  return normalized.length > 0 ? normalized : null
}

const normalizeWooCouponMutationNotice = (
  rawPayload: unknown,
  fallbackMessage: string
): { ok: boolean; message?: string } => {
  const content = typeof rawPayload === "string" ? rawPayload.trim() : ""

  if (!content || content === "-1") {
    return {
      ok: false,
      message: fallbackMessage,
    }
  }

  const isErrorNotice =
    content.includes("woocommerce-error") || content.includes("is-error")
  const message = toNoticeMessage(content)

  if (isErrorNotice) {
    return {
      ok: false,
      message: message ?? fallbackMessage,
    }
  }

  return {
    ok: true,
    message: message ?? undefined,
  }
}

const normalizeWooShippingCalculatorNotice = (
  rawPayload: unknown,
  fallbackMessage: string
): { ok: boolean; message?: string } => {
  const content = typeof rawPayload === "string" ? rawPayload.trim() : ""

  if (!content) {
    return {
      ok: true,
    }
  }

  const isErrorNotice =
    content === "-1" ||
    content.includes("woocommerce-error") ||
    content.includes("is-error")
  const message = toNoticeMessage(content)

  if (isErrorNotice) {
    return {
      ok: false,
      message: message ?? fallbackMessage,
    }
  }

  return {
    ok: true,
    message: message ?? undefined,
  }
}

const fetchWooCartNonce = async (
  sessionToken: string,
  nonceMatcher: RegExp | RegExp[],
  missingNonceErrorMessage: string,
  cartToken?: string | null
): Promise<{
  nonce: string
  upstreamSetCookie: string[]
  cartToken: string | null
}> => {
  const endpoint = `${toWordPressOrigin(env.WP_API_URL)}/carrinho/`
  const response = await axios.get<string>(endpoint, {
    headers: buildWordPressRequestHeaders(sessionToken, cartToken),
  })

  const html = typeof response.data === "string" ? response.data : ""
  const nonce = extractCartNonce(html, nonceMatcher)

  if (!nonce) {
    throw new Error(missingNonceErrorMessage)
  }

  const responseHeaders = response.headers as Record<string, string | string[]>
  const metadata = normalizeResponseMetadata(responseHeaders)

  return {
    nonce,
    upstreamSetCookie: metadata.upstreamSetCookie,
    cartToken: metadata.cartToken,
  }
}

export const addItemToWordPressCart = async (
  input: AddCartItemInput,
  sessionToken: string
): Promise<AddCartItemResult> => {
  const body = new URLSearchParams()

  body.set("action", "xoo_wsc_add_to_cart")
  body.set("add-to-cart", `${input.productId}`)
  body.set("quantity", `${Math.max(1, input.quantity)}`)

  if (input.variationId) {
    body.set("variation_id", `${input.variationId}`)
  }

  for (const [key, value] of Object.entries(input.attributes ?? {})) {
    body.set(key, value)
  }

  const endpoint = `${toWordPressOrigin(env.WP_API_URL)}/?wc-ajax=xoo_wsc_add_to_cart`

  const response = await axios.post(endpoint, body.toString(), {
    headers: buildWordPressRequestHeaders(
      sessionToken,
      undefined,
      "application/x-www-form-urlencoded"
    ),
  })

  const data = response.data as { fragments?: Record<string, string> }
  const responseHeaders = response.headers as Record<string, string | string[]>
  const metadata = normalizeResponseMetadata(responseHeaders)

  const fragments = (data?.fragments ?? {}) as Record<string, string>

  return {
    ok: Boolean(data?.fragments),
    cartCount: normalizeCount(fragments),
    notices: [],
    fragments,
    upstreamSetCookie: metadata.upstreamSetCookie,
    cartToken: metadata.cartToken,
  }
}

export const getWordPressCart = async (
  sessionToken: string,
  cartToken?: string | null
): Promise<CartSnapshot> => {
  const endpoint = `${toWordPressOrigin(env.WP_API_URL)}/wp-json/wc/store/v1/cart`

  const response = await axios.get<WooStoreCartResponse>(endpoint, {
    headers: buildWordPressRequestHeaders(sessionToken, cartToken),
  })

  const data = response.data
  const responseHeaders = response.headers as Record<string, string | string[]>
  const metadata = normalizeResponseMetadata(responseHeaders)

  const minorUnit = data.totals?.currency_minor_unit ?? 2
  const currency = data.totals?.currency_code ?? "BRL"
  const items = (data.items ?? []).map((item) => {
    const itemMinorUnit = item.prices?.currency_minor_unit ?? minorUnit

    return {
      key: item.key ?? "",
      productId: item.id ?? 0,
      name: item.name ?? "Produto",
      quantity: item.quantity ?? 1,
      price: toDecimalCurrency(item.prices?.price, itemMinorUnit),
      lineTotal: toDecimalCurrency(item.totals?.line_total, itemMinorUnit),
      thumbnail: item.images?.[0]?.src,
    }
  })

  const cartCount = items.reduce(
    (accumulator, item) => accumulator + item.quantity,
    0
  )

  return {
    ok: true,
    cartCount,
    items,
    totals: {
      subtotal: toDecimalCurrency(data.totals?.total_items, minorUnit),
      discountTotal: toDecimalCurrency(data.totals?.total_discount, minorUnit),
      total: toDecimalCurrency(data.totals?.total_price, minorUnit),
      currency,
    },
    coupons: (data.coupons ?? []).map((coupon) => {
      return {
        code: coupon.code ?? "",
        discountTotal: toDecimalCurrency(
          coupon.totals?.total_discount,
          minorUnit
        ),
      }
    }),
    shippingAddress: {
      country: data.shipping_address?.country ?? "",
      state: data.shipping_address?.state ?? "",
      city: data.shipping_address?.city ?? "",
      postcode: data.shipping_address?.postcode ?? "",
    },
    upstreamSetCookie: metadata.upstreamSetCookie,
    cartToken: metadata.cartToken,
  }
}

export const applyWordPressCartCoupon = async (
  input: CartCouponInput,
  sessionToken: string
): Promise<CartCouponMutationResult> => {
  const nonceResult = await fetchWooCartNonce(
    sessionToken,
    APPLY_COUPON_NONCE_REGEX,
    "Coupon nonce not found in cart page"
  )

  const endpoint = `${toWordPressOrigin(env.WP_API_URL)}/?wc-ajax=apply_coupon`
  const body = new URLSearchParams()
  body.set("coupon_code", input.code)
  body.set("security", nonceResult.nonce)

  const response = await axios.post(endpoint, body.toString(), {
    headers: buildWordPressRequestHeaders(
      sessionToken,
      undefined,
      "application/x-www-form-urlencoded; charset=UTF-8"
    ),
  })

  const responseHeaders = response.headers as Record<string, string | string[]>
  const metadata = normalizeResponseMetadata(responseHeaders)
  const notice = normalizeWooCouponMutationNotice(
    response.data,
    "Nao foi possivel aplicar o cupom."
  )

  return {
    ok: notice.ok,
    message: notice.message,
    upstreamSetCookie: [
      ...nonceResult.upstreamSetCookie,
      ...metadata.upstreamSetCookie,
    ],
    cartToken: metadata.cartToken,
  }
}

export const removeWordPressCartCoupon = async (
  input: CartCouponInput,
  sessionToken: string
): Promise<CartCouponMutationResult> => {
  const nonceResult = await fetchWooCartNonce(
    sessionToken,
    REMOVE_COUPON_NONCE_REGEX,
    "Coupon nonce not found in cart page"
  )

  const endpoint = `${toWordPressOrigin(env.WP_API_URL)}/?wc-ajax=remove_coupon`
  const body = new URLSearchParams()
  body.set("coupon", input.code)
  body.set("security", nonceResult.nonce)

  const response = await axios.post(endpoint, body.toString(), {
    headers: buildWordPressRequestHeaders(
      sessionToken,
      undefined,
      "application/x-www-form-urlencoded; charset=UTF-8"
    ),
  })

  const responseHeaders = response.headers as Record<string, string | string[]>
  const metadata = normalizeResponseMetadata(responseHeaders)
  const notice = normalizeWooCouponMutationNotice(
    response.data,
    "Nao foi possivel remover o cupom."
  )

  return {
    ok: notice.ok,
    message: notice.message,
    upstreamSetCookie: [
      ...nonceResult.upstreamSetCookie,
      ...metadata.upstreamSetCookie,
    ],
    cartToken: metadata.cartToken,
  }
}

export const updateWordPressCartItemQuantity = async (
  input: UpdateCartItemQuantityInput,
  sessionToken: string,
  cartToken?: string | null
): Promise<CartMutationResult> => {
  const endpoint = `${toWordPressOrigin(env.WP_API_URL)}/?wc-ajax=xoo_wsc_update_item_quantity`

  const body = new URLSearchParams()
  body.set("cart_key", input.key)
  body.set("qty", `${input.quantity}`)

  const response = await axios.post(endpoint, body.toString(), {
    headers: buildWordPressRequestHeaders(
      sessionToken,
      cartToken,
      "application/x-www-form-urlencoded"
    ),
  })

  const responseHeaders = response.headers as Record<string, string | string[]>
  const metadata = normalizeResponseMetadata(responseHeaders)

  return {
    ok: true,
    upstreamSetCookie: metadata.upstreamSetCookie,
    cartToken: metadata.cartToken,
  }
}

export const updateWordPressCartShippingAddress = async (
  input: CartShippingAddressInput,
  sessionToken: string,
  cartToken?: string | null
): Promise<CartShippingAddressMutationResult> => {
  const nonceResult = await fetchWooCartNonce(
    sessionToken,
    SHIPPING_CALCULATOR_NONCE_REGEXES,
    "Shipping calculator nonce not found in cart page",
    cartToken
  )

  const endpoint = `${toWordPressOrigin(env.WP_API_URL)}/carrinho/`
  const effectiveCartToken = nonceResult.cartToken ?? cartToken
  const body = new URLSearchParams()
  body.set("calc_shipping_country", input.country)
  body.set("calc_shipping_state", input.state)
  body.set("calc_shipping_city", input.city)
  body.set("calc_shipping_postcode", input.postcode)
  body.set("woocommerce-shipping-calculator-nonce", nonceResult.nonce)
  body.set("_wp_http_referer", "/carrinho/")
  body.set("calc_shipping", "x")

  const response = await axios.post(endpoint, body.toString(), {
    headers: buildWordPressRequestHeaders(
      sessionToken,
      effectiveCartToken,
      "application/x-www-form-urlencoded; charset=UTF-8"
    ),
  })

  const responseHeaders = response.headers as Record<string, string | string[]>
  const metadata = normalizeResponseMetadata(responseHeaders)
  const notice = normalizeWooShippingCalculatorNotice(
    response.data,
    "Nao foi possivel atualizar o endereco de entrega."
  )

  return {
    ok: notice.ok,
    message: notice.message,
    upstreamSetCookie: [
      ...nonceResult.upstreamSetCookie,
      ...metadata.upstreamSetCookie,
    ],
    cartToken: metadata.cartToken ?? nonceResult.cartToken,
  }
}

export const removeWordPressCartItem = async (
  input: RemoveCartItemInput,
  sessionToken: string,
  cartToken?: string | null
): Promise<CartMutationResult> => {
  const endpoint = `${toWordPressOrigin(env.WP_API_URL)}/wp-json/wc/store/v1/cart/items/${input.key}`

  const response = await axios.delete(endpoint, {
    headers: buildWordPressRequestHeaders(sessionToken, cartToken),
  })

  const responseHeaders = response.headers as Record<string, string | string[]>
  const metadata = normalizeResponseMetadata(responseHeaders)

  return {
    ok: true,
    upstreamSetCookie: metadata.upstreamSetCookie,
    cartToken: metadata.cartToken,
  }
}
